import React, { useCallback, useEffect, useState } from "react";
import { createPortal } from "react-dom";
import DatePicker from "react-datepicker";
import ConfirmModal from "../../common/ConfirmModal";
import Dropdown from "../../common/dropDown";
import SnomedSearchField from "./SnomedSearchField";
import {
  archiveMedicationHistory,
  loadParticipantClinicalPack,
  saveMedicationHistory,
} from "./participantService";
import {
  displayOrDash,
  parseUiDate,
  pickField,
  toApiDate,
} from "./clinicalHistoryUtils";

export function medId(row) {
  return String(pickField(row, "MedicationHistoryId") || "").trim();
}

export function MedicationModal({
  open,
  initial,
  userData,
  medicalIndications,
  saving,
  onClose,
  onSave,
}) {
  const [medication, setMedication] = useState(null);
  const [dose, setDose] = useState("");
  const [unit, setUnit] = useState(null);
  const [frequency, setFrequency] = useState(null);
  const [route, setRoute] = useState(null);
  const [indication, setIndication] = useState("");
  const [indicationCode, setIndicationCode] = useState("");
  const [startDate, setStartDate] = useState(null);
  const [endDate, setEndDate] = useState(null);

  useEffect(() => {
    if (!open) return;
    const name = String(pickField(initial, "MedicationName") || "").trim();
    const code = String(
      pickField(initial, "MedicationSnomedCode", "SnomedCode") || "",
    ).trim();
    setMedication(name ? { term: name, code } : null);
    setDose(String(pickField(initial, "Dose") || ""));
    const unitTerm = String(pickField(initial, "Unit") || "").trim();
    setUnit(
      unitTerm
        ? {
            term: unitTerm,
            code: String(pickField(initial, "UnitSnomedCode") || ""),
          }
        : null,
    );
    const freqTerm = String(pickField(initial, "Frequency") || "").trim();
    setFrequency(
      freqTerm
        ? {
            term: freqTerm,
            code: String(pickField(initial, "FrequencySnomedCode") || ""),
          }
        : null,
    );
    const routeTerm = String(pickField(initial, "Route") || "").trim();
    setRoute(routeTerm ? { term: routeTerm, code: "" } : null);
    setIndication(String(pickField(initial, "Indication") || "").trim());
    setIndicationCode(
      String(pickField(initial, "IndicationSnomedCode") || "").trim(),
    );
    setStartDate(parseUiDate(pickField(initial, "StartDate")));
    setEndDate(parseUiDate(pickField(initial, "ResolutionDate")));
  }, [initial, open]);

  if (!open) return null;
  const editing = Boolean(initial && medId(initial));

  return createPortal(
    <div className="pm-clinical-modal" role="dialog" aria-modal="true">
      <div className="pm-clinical-modal__dialog pm-clinical-modal__dialog--wide">
        <header className="pm-clinical-modal__header">
          <h3 className="pm-clinical-modal__title">
            {editing ? "Edit medication" : "Add medication"}
          </h3>
          <button
            type="button"
            className="btn-close"
            aria-label="Close"
            disabled={saving}
            onClick={onClose}
          />
        </header>
        <div className="pm-clinical-modal__body">
          <SnomedSearchField
            userData={userData}
            termType="Medication"
            label="Medication"
            selected={medication}
            onSelect={setMedication}
            disabled={saving}
          />

          <div className="pm-clinical-modal__row">
            <div className="pm-clinical-modal__field">
              <label className="pm-clinical-modal__label">Dose</label>
              <input
                className="form-control form-control-sm"
                value={dose}
                disabled={saving}
                onChange={(e) => setDose(e.target.value)}
              />
            </div>
            <SnomedSearchField
              userData={userData}
              termType="Unit"
              label="Unit"
              selected={unit}
              onSelect={setUnit}
              disabled={saving}
              minChars={0}
            />
          </div>

          <div className="pm-clinical-modal__row">
            <SnomedSearchField
              userData={userData}
              termType="Frequency"
              label="Frequency"
              selected={frequency}
              onSelect={setFrequency}
              disabled={saving}
              minChars={0}
            />
            <SnomedSearchField
              userData={userData}
              termType="Route"
              label="Route"
              selected={route}
              onSelect={setRoute}
              disabled={saving}
              minChars={0}
            />
          </div>

          <div className="pm-clinical-modal__field">
            <label className="pm-clinical-modal__label">Indication</label>
            <Dropdown
              options={[
                {
                  value: "",
                  label: "Select indication (from medical history)",
                },
                ...medicalIndications
                  .map((m) => {
                    const term = String(
                      pickField(m, "SnomedTerm") || "",
                    ).trim();
                    if (!term) return null;
                    return {
                      value: term,
                      label: term,
                    };
                  })
                  .filter(Boolean),
              ]}
              selected={
                indication
                  ? { value: indication, label: indication }
                  : {
                      value: "",
                      label: "Select indication (from medical history)",
                    }
              }
              onSelect={(opt) => {
                const value = String(opt?.value || "");
                setIndication(value);
                const match = medicalIndications.find(
                  (m) => String(pickField(m, "SnomedTerm") || "") === value,
                );
                setIndicationCode(
                  String(
                    pickField(match, "SnomedCode", "SnomedId") || "",
                  ).trim(),
                );
              }}
              showSearch
              disabled={saving}
            />
          </div>

          <div className="pm-clinical-modal__row">
            <div className="pm-clinical-modal__field">
              <label className="pm-clinical-modal__label">Start date</label>
              <DatePicker
                selected={startDate}
                onChange={setStartDate}
                dateFormat="dd/MM/yyyy"
                className="form-control form-control-sm"
                placeholderText="DD/MM/YYYY"
                disabled={saving}
                isClearable
                showMonthDropdown
                showYearDropdown
                dropdownMode="select"
                todayButton="Today"
                minDate={new Date(1920, 0, 1)}
                maxDate={new Date()}
                popperProps={{ strategy: "fixed" }}
              />
            </div>
            <div className="pm-clinical-modal__field">
              <label className="pm-clinical-modal__label">End date</label>
              <DatePicker
                selected={endDate}
                onChange={setEndDate}
                dateFormat="dd/MM/yyyy"
                className="form-control form-control-sm"
                placeholderText="DD/MM/YYYY"
                disabled={saving}
                isClearable
                showMonthDropdown
                showYearDropdown
                dropdownMode="select"
                todayButton="Today"
                minDate={startDate || new Date(1920, 0, 1)}
                maxDate={new Date()}
                popperProps={{ strategy: "fixed" }}
              />
            </div>
          </div>
        </div>
        <footer className="pm-clinical-modal__footer">
          <button
            type="button"
            className="btn btn-sm btn-outline-secondary"
            disabled={saving}
            onClick={onClose}
          >
            Cancel
          </button>
          <button
            type="button"
            className="btn btn-sm btn-primary"
            disabled={saving || !medication?.term}
            onClick={() =>
              onSave({
                medication,
                dose,
                unit,
                frequency,
                route,
                indication,
                indicationCode,
                startDate,
                endDate,
              })
            }
          >
            {saving ? "Saving…" : editing ? "Update" : "Save"}
          </button>
        </footer>
      </div>
    </div>,
    document.body,
  );
}

function MedicationHistoryPanel({
  userData,
  volunteerId,
  canEdit = false,
  showAlert,
  showLoader,
  hideLoader,
}) {
  const [rows, setRows] = useState([]);
  const [medicalIndications, setMedicalIndications] = useState([]);
  const [loading, setLoading] = useState(false);
  const [modalOpen, setModalOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [saving, setSaving] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [deleting, setDeleting] = useState(false);

  const reload = useCallback(async () => {
    if (!userData?.OrganizationId || !volunteerId) return;
    setLoading(true);
    try {
      const pack = await loadParticipantClinicalPack(userData, volunteerId);
      setRows(Array.isArray(pack.medication) ? pack.medication : []);
      setMedicalIndications(Array.isArray(pack.medical) ? pack.medical : []);
    } catch {
      setRows([]);
      setMedicalIndications([]);
    } finally {
      setLoading(false);
    }
  }, [userData, volunteerId]);

  useEffect(() => {
    reload();
  }, [reload]);

  const openAdd = () => {
    setEditing(null);
    setModalOpen(true);
  };

  const openEdit = (row) => {
    setEditing(row);
    setModalOpen(true);
  };

  const handleSave = async (form) => {
    if (!form?.medication?.term) {
      showAlert?.({ message: "Please select a medication." });
      return;
    }
    if (
      form.startDate &&
      form.endDate &&
      form.endDate < form.startDate
    ) {
      showAlert?.({
        message: "End date cannot be before start date.",
        type: "warning",
      });
      return;
    }

    const id = editing ? medId(editing) : "";
    const existingStudyId =
      editing?.StudyIdRef ??
      editing?.studyIdRef ??
      editing?.StudyId ??
      editing?.studyId ??
      null;
    const existingSiteId =
      editing?.SiteIdRef ??
      editing?.siteIdRef ??
      editing?.SiteId ??
      editing?.siteId ??
      null;
    setSaving(true);
    showLoader?.("Saving medication…");
    try {
      await saveMedicationHistory(userData, {
        MedicationHistoryId: id || null,
        VolunteerId: volunteerId,
        MedicationName: form.medication.term,
        MedicationSnomedCode: form.medication.code || null,
        Route: form.route?.term || "",
        Dose: form.dose || "",
        Unit: form.unit?.term || "",
        UnitSnomedCode: form.unit?.code || "",
        Frequency: form.frequency?.term || "",
        FrequencySnomedCode: form.frequency?.code || "",
        Indication: form.indication || "",
        IndicationSnomedCode: form.indicationCode || "",
        StartDate: toApiDate(form.startDate),
        ResolutionDate: toApiDate(form.endDate),
        IsArchive: false,
        ActionType: "InsertUpdate",
        StudyIdRef: id ? existingStudyId || null : null,
        SiteIdRef: id ? existingSiteId || null : null,
      });
      setModalOpen(false);
      setEditing(null);
      showAlert?.({ message: "Medication saved.", type: "success" });
      await reload();
    } catch (err) {
      showAlert?.({ message: err?.message || "Could not save medication." });
    } finally {
      hideLoader?.();
      setSaving(false);
    }
  };

  const handleConfirmDelete = async () => {
    if (!deleteTarget) return;
    setDeleting(true);
    showLoader?.("Deleting medication…");
    try {
      await archiveMedicationHistory(userData, {
        medicationHistoryId: deleteTarget,
        volunteerId,
      });
      setDeleteTarget(null);
      showAlert?.({ message: "Medication deleted.", type: "success" });
      await reload();
    } catch (err) {
      showAlert?.({ message: err?.message || "Could not delete medication." });
    } finally {
      hideLoader?.();
      setDeleting(false);
    }
  };

  return (
    <div className="pm-clinical-tab">
      <div className="pm-clinical-tab__head">
        <div>
          <h3 className="pm-details-section__title mb-0">Medication history</h3>
          <p className="pm-clinical-tab__sub">
            Current and past medications recorded for this participant.
          </p>
        </div>
        <div className="pm-clinical-tab__head-actions">
          {loading ? (
            <span className="pm-clinical-tab__muted">Loading…</span>
          ) : null}
          {canEdit ? (
            <button
              type="button"
              className="btn btn-sm btn-primary"
              onClick={openAdd}
            >
              <i className="fas fa-plus me-1" aria-hidden="true" />
              Add medication
            </button>
          ) : null}
        </div>
      </div>

      {rows.length ? (
        <div className="pm-clinical-panel">
          <div className="pm-clinical-cards pm-thin-scroll">
            {rows.map((row, index) => {
              const id = medId(row);
              const dose = displayOrDash(pickField(row, "Dose"));
              const unit = displayOrDash(pickField(row, "Unit"));
              const doseLabel =
                dose !== "—" || unit !== "—"
                  ? [dose !== "—" ? dose : null, unit !== "—" ? unit : null]
                      .filter(Boolean)
                      .join(" ")
                  : "—";
              return (
                <article key={id || index} className="pm-clinical-card">
                  <div className="pm-clinical-card__icon" aria-hidden="true">
                    <i className="fas fa-pills" />
                  </div>
                  <div className="pm-clinical-card__body">
                    <h4 className="pm-clinical-card__title">
                      {displayOrDash(pickField(row, "MedicationName"))}
                    </h4>
                    <div className="pm-clinical-card__meta">
                      <span>{doseLabel}</span>
                      <span>
                        {displayOrDash(pickField(row, "Frequency"))}
                      </span>
                      <span>{displayOrDash(pickField(row, "Route"))}</span>
                      <span>
                        {displayOrDash(pickField(row, "StartDate"))}
                        {" → "}
                        {displayOrDash(pickField(row, "ResolutionDate"))}
                      </span>
                      {pickField(row, "Indication") ? (
                        <span>
                          {displayOrDash(pickField(row, "Indication"))}
                        </span>
                      ) : null}
                    </div>
                  </div>
                  {canEdit ? (
                    <div className="pm-clinical-card__actions">
                      <button
                        type="button"
                        className="pm-clinical-card__btn"
                        title="Edit"
                        aria-label="Edit"
                        onClick={() => openEdit(row)}
                      >
                        <i className="fas fa-pencil-alt" />
                      </button>
                      <button
                        type="button"
                        className="pm-clinical-card__btn is-danger"
                        title="Delete"
                        aria-label="Delete"
                        disabled={!id}
                        onClick={() => setDeleteTarget(id)}
                      >
                        <i className="fas fa-trash-alt" />
                      </button>
                    </div>
                  ) : null}
                </article>
              );
            })}
          </div>
        </div>
      ) : (
        <div className="pm-clinical-empty">
          <div className="pm-clinical-empty__icon" aria-hidden="true">
            <i className="fas fa-pills" />
          </div>
          <p className="pm-clinical-empty__title">No medication history yet</p>
          <p className="pm-clinical-empty__text">
            {canEdit
              ? "Add the first medication to start this record."
              : "Nothing recorded yet."}
          </p>
          {canEdit ? (
            <button
              type="button"
              className="btn btn-sm btn-primary"
              onClick={openAdd}
            >
              <i className="fas fa-plus me-1" aria-hidden="true" />
              Add medication
            </button>
          ) : null}
        </div>
      )}

      <MedicationModal
        open={modalOpen}
        initial={editing}
        userData={userData}
        medicalIndications={medicalIndications}
        saving={saving}
        onClose={() => {
          if (!saving) {
            setModalOpen(false);
            setEditing(null);
          }
        }}
        onSave={handleSave}
      />

      <ConfirmModal
        isOpen={Boolean(deleteTarget)}
        onClose={() => !deleting && setDeleteTarget(null)}
        onConfirm={handleConfirmDelete}
        title="Delete medication"
        message="Are you sure you want to delete this medication record?"
        confirmText={deleting ? "Deleting…" : "Delete"}
        confirmButtonClass="btn-danger"
        icon="fas fa-trash-alt text-danger"
      />
    </div>
  );
}

export default MedicationHistoryPanel;
